import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { IS_NAV_COLLAPSED } from 'src/app/core/constants/local-storage-keys';
import { EXTERNAL_NAV_URLS } from 'src/app/core/constants/mapping-keys';
import { EntityType } from 'src/app/core/models/entitlements/entity';
import { Patient } from 'src/app/core/models/patient';
import { AuthService } from 'src/app/core/services/auth.service';
import { MemberInformationService } from 'src/app/core/services/member-information.service';
import { allUrls, anyUrls, ExternalNavItem, SecondaryNavItem } from 'src/app/core/utils/routing-util';
import { EnvironmentService } from './environment.service';
import { OrganizationService } from './organization.service';

export const MEMBER_HOME_LABEL = 'Member Home';

@Injectable({
  providedIn: 'root'
})
export class SideNavService {
  patient: Patient;

  private _externalNavItems: BehaviorSubject<ExternalNavItem[]> = new BehaviorSubject([]);
  externalNavItems: Observable<ExternalNavItem[]> = this._externalNavItems.asObservable();

  private _patientNav: BehaviorSubject<SecondaryNavItem[]> = new BehaviorSubject([]);
  patientNav: Observable<SecondaryNavItem[]> = this._patientNav.asObservable();

  private _adminHubNav: BehaviorSubject<SecondaryNavItem[]> = new BehaviorSubject([]);
  adminHubNav: Observable<SecondaryNavItem[]> = this._adminHubNav.asObservable();

  private get isNavCollapsed(): boolean {
    return JSON.parse(localStorage.getItem(this.isNavCollapsedStorageKey) || 'false');
  }

  private adminHubNavItems: SecondaryNavItem[] = [];
  private isAdminHubOpen = new BehaviorSubject<boolean>(false);

  getAdminHubOpen(): boolean {
    return this.isAdminHubOpen.value;
  }

  setAdminHubOpen(value: boolean): void {
    this.isAdminHubOpen.next(value);
  }

  private set isNavCollapsed(isNavCollapsed: boolean) {
    if (!this.isPatientNavPresent) {
      localStorage.setItem(this.isNavCollapsedStorageKey, JSON.stringify(isNavCollapsed));
    }

    if (!this.isAdminHubNavPresent) {
      localStorage.setItem(this.isNavCollapsedStorageKey, JSON.stringify(isNavCollapsed));
    }

    this._navCollapsed.next(isNavCollapsed);

    // Dispatching a window resize event to force ngx-datatable to recalculate table size/column widths.
    // This will not work without the setTimeout() wrapper.
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  }

  private _navCollapsed: BehaviorSubject<boolean> = new BehaviorSubject(this.isNavCollapsed);
  navCollapsed: Observable<boolean> = this._navCollapsed.asObservable();

  private get isPatientNavPresent(): boolean {
    return this._patientNav.value?.length > 0;
  }

  private get isAdminHubNavPresent(): boolean {
    return this._adminHubNav.value?.length > 0;
  }

  private get isNavCollapsedStorageKey(): string {
    return `${IS_NAV_COLLAPSED}${this.environmentService.apiBaseUrl}`;
  }

  constructor(
    memberInformationService: MemberInformationService,
    router: Router,
    public authService: AuthService,
    private organizationService: OrganizationService,
    private environmentService: EnvironmentService
  ) {
    const memberIdObservable: Observable<string> = memberInformationService.memberId;
    const navigationEndObservable: Observable<NavigationEnd> = router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd)
    );

    combineLatest([memberIdObservable, navigationEndObservable]).subscribe(
      ([memberId, navigationEndEvent]: [string, NavigationEnd]) => {
        const isPatientNavRoute =
          memberId &&
          navigationEndEvent.url.startsWith(`/private/patients/${memberId}`) &&
          this.authService.entity.entityType !== EntityType.PATIENT;

        const isAdminNavRoute = navigationEndEvent.url.startsWith(`/private/admin-hub`);

        if (isPatientNavRoute) {
          this.usePatientSideNav(memberId);
          this.setNavCollapsed(false);
        } else if (isAdminNavRoute) {
          this.useAdminHubNav();
          this.setNavCollapsed(false);
        } else {
          this.usePrimaryNav();

          this.setNavCollapsed(this.isNavCollapsed);
        }
      }
    );
  }

  toggleNavCollapsed() {
    this.isNavCollapsed = !this._navCollapsed.value;
  }

  setNavCollapsed(isNavCollapsed: boolean) {
    this.isNavCollapsed = isNavCollapsed;
  }

  usePrimaryNav() {
    this._patientNav.next([]);
    this._adminHubNav.next([]);

    if (this.authService.isAuthenticated()) {
      const externalNavUrlsMappingKey = this.organizationService.getCachedMappingKey(EXTERNAL_NAV_URLS);

      if (externalNavUrlsMappingKey) {
        const externalNavItems = JSON.parse(externalNavUrlsMappingKey) as ExternalNavItem[];

        this._externalNavItems.next(
          (externalNavItems || []).filter((navItem) => this.authService.isEntitled(navItem.entitlementUrl as string))
        );
      }
    }
  }

  useAdminHubNav() {
    this._patientNav.next([]);
    const adminHubNav: SecondaryNavItem[] = [
      {
        id: 'Nav-AdminHub-Users',
        routerLink: `/private/admin-hub/users/search`,
        displayText: 'Users',
        entitlementUrl: '/users/viewIndividualUsers.html'
      },
      {
        id: 'Nav-AdminHub-Notifications',
        routerLink: `/private/admin-hub/app-notifications`,
        displayText: 'Configure Application Notifications',
        entitlementUrl: allUrls(
          '/systemNotifications/fetchAll',
          '/systemNotifications/saveNotification',
          '/systemNotifications/editNotification',
          '/systemNotifications/deleteNotification',
          '/systemNotifications/saveFollowUp',
          '/systemNotifications/editFollowUp',
          '/systemNotifications/deleteFollowUp',
          '/notificationTemplate/fetchTemplates',
          '/notificationTemplate/saveTemplate',
          '/notificationTemplate/editTemplate',
          '/notificationTemplate/deleteTemplate'
        )
      },
      {
        id: 'Nav-AdminHub-Reports',
        routerLink: `/private/admin-hub/download-reports`,
        displayText: 'Download Reports',
        entitlementUrl: anyUrls('/active-users/excel.html', '/user-audit/excel.html')
      },
      {
        id: 'Nav-AdminHub-Assessments',
        routerLink: `/private/admin-hub/assessments/maintain`,
        displayText: 'Maintain Assessments',
        entitlementUrl: '/assessment/displaymaintainassessments.html'
      },
      {
        id: 'Nav-AdminHub-Employers',
        routerLink: `/private/admin-hub/manage-employers`,
        displayText: 'Manage Employers',
        entitlementUrl: '/employer/addEmployer.html'
      },
      {
        id: 'Nav-AdminHub-Resources',
        routerLink: `/private/admin-hub/manage-resources`,
        displayText: 'Manage Resources',
        entitlementUrl: '/resource/saveResource.html'
      },
      {
        id: 'Nav-AdminHub-Worklists',
        routerLink: `/private/admin-hub/add-edit-worklist`,
        displayText: 'Maintain Worklists',
        entitlementUrl: '/worklist/fetchAllWorklists.html'
      },
      {
        id: 'Nav-AdminHub-QuartzJobs',
        routerLink: `/private/admin-hub/quartz-jobs`,
        displayText: 'Quartz Jobs',
        entitlementUrl: '/quartz/trigger-job'
      },
      {
        id: 'Nav-AdminHub-ManualPatientEdits',
        routerLink: `/private/admin-hub/manual-patient-edits`,
        displayText: 'Update Member Data History',
        entitlementUrl: '/patients/manualPatientUpdates'
      }
    ];

    this._adminHubNav.next(adminHubNav);
    this.adminHubNavItems = adminHubNav;
  }

  usePatientSideNav(memberId: string) {
    const patientNav: SecondaryNavItem[] = [
      {
        id: 'Nav-Patient-Home',
        routerLink: `/private/patients/${memberId}/details`,
        displayText: MEMBER_HOME_LABEL,
        entitlementUrl: '/patients/viewPatientDetail.html'
      },
      {
        id: 'Nav-Patient-BaseballCard',
        routerLink: `/private/patients/${memberId}/baseball-card`,
        displayText: 'MHN Baseball Card',
        entitlementUrl: '/baseballcard/getBaseballCard.html'
      },
      {
        id: 'Nav-Patient-Allergies',
        routerLink: `/private/patients/${memberId}/allergies`,
        displayText: 'Allergies',
        entitlementUrl: '/patients/getAllergies.html'
      },
      {
        id: 'Nav-Patient-ActionPlans',
        routerLink: `/private/patients/${memberId}/all-action-plans`,
        displayText: 'Action Plans',
        entitlementUrl: anyUrls(
          '/sdohactionplan/fetchSDOHActionPlans.html',
          '/maternityactionplan/fetchMaternityActionPlans.html',
          '/wellnessrecoveryactionplan/fetchWellnessRecoveryActionPlans.html'
        )
      },
      {
        id: 'Nav-Patient-Appointments',
        routerLink: `/private/patients/${memberId}/appointments`,
        displayText: 'Appointments',
        entitlementUrl: '/appointment/fetchAppointments.html'
      },
      {
        id: 'Nav-Patient-Assessments',
        routerLink: `/private/patients/${memberId}/assessments`,
        displayText: 'Assessments',
        entitlementUrl: '/assessment/displaymemberassessments.html'
      },
      {
        id: 'Nav-Patient-Authorizations',
        routerLink: `/private/patients/${memberId}/authorizations`,
        displayText: 'Authorizations',
        entitlementUrl: '/authorization/retrieveMemberAuthorizationRecords.html'
      },
      {
        id: 'Nav-Patient-CareGaps',
        routerLink: `/private/patients/${memberId}/care-gaps`,
        displayText: 'Care Gaps',
        entitlementUrl: '/patients/careReminderSchedule.html'
      },
      {
        id: 'Nav-Patient-CarePlans',
        routerLink: `/private/patients/${memberId}/care-plans`,
        displayText: 'Care Plans',
        entitlementUrl: '/careplan/displayMaintaincareplan.html'
      },
      {
        id: 'Nav-Patient-CareTeam',
        routerLink: `/private/patients/${memberId}/build-care-team`,
        displayText: 'Care Team',
        entitlementUrl: '/patients/buildCareTeam.html'
      },
      {
        id: 'Nav-Patient-CaseReview',
        routerLink: `/private/patients/${memberId}/case-review`,
        displayText: 'Case Review',
        entitlementUrl: anyUrls(
          '/case-review/redirect.html',
          '/depression-case-review/fetchAllDepressionCaseReview.html'
        )
      },
      {
        id: 'Nav-Patient-CCActionPlans',
        routerLink: `/private/patients/${memberId}/action-plans`,
        displayText: 'CC Action Plans',
        entitlementUrl: '/actionplan/fetchFilledQuestionnaireActionPlans.html'
      },
      {
        id: 'Nav-Patient-CCMStatus',
        routerLink: `/private/patients/${memberId}/chronic-care-management-status`,
        displayText: 'Chronic Care Management Status',
        entitlementUrl: '/memberfacet/ccm/getAddedMemberFacetStatuses.html'
      },
      {
        id: 'Nav-Patient-Claims',
        routerLink: `/private/patients/${memberId}/claims`,
        displayText: 'Claims',
        entitlementUrl: '/claims/getClaims.html'
      },
      {
        id: 'Nav-Patient-ContactLogs',
        routerLink: `/private/patients/${memberId}/contact-logs-search`,
        displayText: 'Contact Log/Progress Note',
        entitlementUrl: '/contactInfo/getPatientContactHistory.html'
      },
      {
        id: 'Nav-Patient-Documents',
        routerLink: `/private/patients/${memberId}/documents`,
        displayText: 'Documents',
        entitlementUrl: '/patientdoc/displayDocumentManager.html'
      },
      {
        id: 'Nav-Patient-Education',
        routerLink: `/private/patients/${memberId}/education-material`,
        displayText: 'Education',
        entitlementUrl: '/education/displayassigneducation.html'
      },
      {
        id: 'Nav-Patient-Eligibility',
        routerLink: `/private/patients/${memberId}/eligibility`,
        displayText: 'Eligibility',
        entitlementUrl: '/patients/getPatientEligibilityHistory.html'
      },
      {
        id: 'Nav-Patient-HospitalActivity',
        routerLink: `/private/patients/${memberId}/hospital-activity`,
        displayText: 'Hospital Activity',
        entitlementUrl: '/patientadmission/retrieveMemberAdmissionRecords.html'
      },
      {
        id: 'Nav-Patient-MedRec',
        routerLink: `/private/patients/${memberId}/med-rec`,
        displayText: 'Medication Reconciliation',
        entitlementUrl: '/bundle/fetchMedicationReconciliations.html'
      },
      {
        id: 'Nav-Patient-MedFills',
        routerLink: `/private/patients/${memberId}/rx-history`,
        displayText: 'Medication Fills',
        entitlementUrl: '/patientmed/rxHistory.html'
      },
      {
        id: 'Nav-Patient-MemberStatus',
        routerLink: `/private/patients/${memberId}/member-status`,
        displayText: 'Member Engagement Status',
        entitlementUrl: '/memberstatus/getAddedMemberStatuses.html'
      },
      {
        id: 'Nav-Patient-Messages',
        routerLink: `/private/patients/${memberId}/messages`,
        displayText: 'Messages',
        entitlementUrl: '/patients/fetchPatientMessages.html'
      },
      {
        id: 'Nav-Patient-PCPCareSheet',
        routerLink: `/private/patients/${memberId}/pcp-care-sheet`,
        displayText: 'PCP Care Sheet',
        entitlementUrl: '/baseballcard/getPCPGaps/pdf.html'
      },
      {
        id: 'Nav-Patient-Programs',
        routerLink: `/private/patients/${memberId}/programs`,
        displayText: 'Programs',
        entitlementUrl: '/patients/viewPatientCondition.html'
      },
      {
        id: 'Nav-Patient-Referrals',
        routerLink: `/private/patients/${memberId}/referrals`,
        displayText: 'Referrals',
        entitlementUrl: '/referral/view_memberReferral.html'
      },
      {
        id: 'Nav-Patient-Risk',
        routerLink: `/private/patients/${memberId}/risk`,
        displayText: 'Risk',
        entitlementUrl: '/patients/fetchRiskHistory.html'
      },
      {
        id: 'Nav-Patient-Tasks',
        routerLink: `/private/patients/${memberId}/worklist`,
        displayText: 'Tasks',
        entitlementUrl: '/careplan/getAllAssignedCareplaninterventions.html'
      },
      {
        id: 'Nav-Patient-Timeline',
        routerLink: `/private/patients/${memberId}/timeline`,
        displayText: 'Timeline',
        entitlementUrl: '/members/timeline.html'
      },
      {
        id: 'Nav-Patient-TOCBundle',
        routerLink: `/private/patients/${memberId}/toc-bundle`,
        displayText: 'TOC Bundle',
        entitlementUrl: '/bundle/fetchTOCBundleTasks.html'
      },
      {
        id: 'Nav-Patient-Vitals',
        routerLink: `/private/patients/${memberId}/vitals`,
        displayText: 'Vitals',
        entitlementUrl: '/patients/getPatientBiometricServices.html'
      },
      {
        id: 'Nav-Patient-VitalsCharts',
        routerLink: `/private/patients/${memberId}/vitals-charts`,
        displayText: 'Vitals Charts',
        entitlementUrl: '/patients/getBiometricsServiceData.html'
      },
      {
        id: 'Nav-Patient-Waivers',
        routerLink: `/private/patients/${memberId}/waivers`,
        displayText: 'Waivers',
        entitlementUrl: '/patients/getPatientWaivers.html'
      }
    ];

    this._patientNav.next(patientNav);
    this._adminHubNav.next([]);
  }
}
